import { Dialog as MuiDialog, DialogProps as MuiDialogProps, Slide as MuiSlide } from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import cx from 'classnames';
import { FC, forwardRef, ForwardRefRenderFunction, ReactElement, Ref, useMemo } from 'react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Loading } from '@shared/components/loading';
import { ContainerWidths } from '@shared/constants/common';
import { UnsavedChangesModal } from '@shared/modals/unsaved-changes-modal';

import { OverlayProvider, UnsavedChangesConfig, useOverlay } from './Overlay.context';
import { styles } from './Overlay.styles';

const DrawerTransition = forwardRef(
  (props: TransitionProps & { children?: ReactElement<any, any> }, ref: Ref<unknown>) => (
    <MuiSlide direction="left" ref={ref} {...props} />
  )
);

const ModalTransition = forwardRef(
  (props: TransitionProps & { children?: ReactElement<any, any> }, ref: Ref<unknown>) => (
    <MuiSlide direction="down" ref={ref} {...props} />
  )
);

export enum OverlayType {
  Drawer = 'drawer',
  FullScreen = 'fullScreen',
  Modal = 'modal',
}

export interface OverlayProps
  extends Omit<MuiDialogProps, 'classes' | 'PaperProps' | 'ref' | 'TransitionComponent' | 'onClose'>,
    WithStyles<typeof styles> {
  backdropScroll?: boolean;
  loading?: boolean;
  ref: Ref<HTMLDivElement>;
  type?: OverlayType;
  width: ContainerWidths;
  withTransition?: boolean;
  // Used for context
  unsavedChanges?: UnsavedChangesConfig;
  onClose: () => any;
}
/**
 * @deprecated Use OverlayProps instead
 */
export type ModalProps = OverlayProps;

const OverlayComponent: FC<OverlayProps> = ({
  backdropScroll,
  children,
  classes,
  loading,
  ref,
  type = OverlayType.Modal,
  width,
  withTransition,
  // Used for context
  onClose,
  unsavedChanges,
  // Rest go to mui dialog
  ...dialogProps
}) => {
  const overlayContext = useOverlay();

  const TransitionComponent = useMemo(() => {
    if (!withTransition) {
      return undefined;
    }

    switch (type) {
      case OverlayType.Drawer:
        return DrawerTransition;
      case OverlayType.FullScreen:
        return undefined;
      case OverlayType.Modal:
        return ModalTransition;
    }
  }, [type, withTransition]);

  const paperPropsStyles = useMemo(() => {
    switch (type) {
      case OverlayType.FullScreen:
        return { maxWidth: ContainerWidths.full };

      default: {
        if (width === ContainerWidths.auto) {
          return { width: 'auto' };
        }

        return { maxWidth: width };
      }
    }
  }, [type, width]);

  return (
    <MuiDialog
      {...dialogProps}
      classes={{
        root: cx(classes.root, classes[type], { [classes.rootDisablePortal]: dialogProps.disablePortal }),
        scrollPaper: cx(classes.scrollPaper, {
          [classes.scrollPaperBackdropScroll]: backdropScroll,
        }),
        paper: cx(classes.paper, { [classes.paperLoading]: loading }),
      }}
      PaperProps={{ style: paperPropsStyles }}
      ref={ref}
      style={{
        ...dialogProps.style,
        ...(dialogProps.disablePortal && { position: 'absolute' }),
      }}
      {...(TransitionComponent && { TransitionComponent })}
      onClose={overlayContext.onCheckAndClose}
    >
      {loading ? <Loading /> : <>{children}</>}

      {overlayContext.unsavedChanges && (
        <UnsavedChangesModal
          classes={{ root: classes.unsavedChangesModalRoot, paper: classes.unsavedChangesModalPaper }}
          content={overlayContext.unsavedChanges.content}
          disablePortal
          labelCancel={overlayContext.unsavedChanges.labelCancel}
          labelConfirm={overlayContext.unsavedChanges.labelConfirm}
          open={overlayContext.confirmOpen}
          style={{ position: 'absolute' }}
          withTransition={withTransition}
          onCancel={() => overlayContext.setConfirmOpen(false)}
          onConfirm={() => {
            overlayContext.setConfirmOpen(false);
            overlayContext.onClose();
          }}
        ></UnsavedChangesModal>
      )}
    </MuiDialog>
  );
};

const OverlayWithContext: ForwardRefRenderFunction<HTMLDivElement, OverlayProps> = (props, ref) => (
  <OverlayProvider unsavedChanges={props.unsavedChanges} onClose={props.onClose}>
    <OverlayComponent {...props} ref={ref} />
  </OverlayProvider>
);

export const Overlay = withStyles(styles)(forwardRef(OverlayWithContext));
/**
 * @deprecated Use Overlay instead
 */
export const Modal = Overlay;
